import { useEffect, useState } from 'react'
import { Avatar, Skeleton, Tooltip } from 'antd'
import { UserOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { errorStatus } from '@/shared/api/errors'
import { fetchImageBlob } from './attendanceApi'

interface AuthedImageProps {
  /** Rasm endpointi, masalan `/api/attendance/employees/{id}/photo` */
  url: string
  /** Rasm umuman bormi. `false` bo'lsa so'rov YUBORILMAYDI. */
  present: boolean
  width: number
  height: number
  alt: string
  /** Rasm bo'lmaganda ko'rsatiladigan matn ("rasm yo'q") */
  emptyHint?: string
  onClick?: () => void
}

/**
 * Himoyalangan rasm — `Authorization` sarlavhasi bilan.
 *
 * <h2>⚠️ Nega oddiy `<img src>` yaramaydi</h2>
 * Davomat rasmlari (`/employees/{id}/photo`, `/events/{id}/picture`)
 * boshqa har qanday endpoint kabi `Bearer` token talab qiladi, brauzer esa
 * `<img>` so'roviga sarlavha qo'sha OLMAYDI — u rasmni tokensiz so'raydi
 * va 401 oladi. Ekranda bu "singan rasm" belgisi bo'lib ko'rinardi va
 * sabab hech qayerda yozilmasdi.
 *
 * Muqobil yechim tokenni URL'ga qo'yish bo'lardi (`?token=...`), lekin u
 * tokenni brauzer tarixiga, proksi loglariga va tashqi havolalarga
 * ketadigan `Referer` sarlavhasiga chiqarardi. Shuning uchun rasm axios
 * orqali `Blob` bo'lib olinadi.
 *
 * <h2>⚠️ `URL.revokeObjectURL` MAJBURIY</h2>
 * Har `createObjectURL` brauzer xotirasida rasmni USHLAB TURADI, va u
 * sahifa yopilgunicha o'zi bo'shamaydi. Hodisalar jadvalida har sahifada
 * o'nlab kadr bor — bir necha daqiqalik varaqlashdan keyin bu yuzlab
 * megabaytga aylanardi va "panel sekinlashib qoldi" degan, sababini
 * topib bo'lmaydigan shikoyat bo'lib qaytardi.
 *
 * <h2>Nega `useQuery` emas</h2>
 * TanStack Query kesh yozuvini o'zi bekor qilganda `Blob` qoladi, lekin
 * undan yasalgan `objectURL` ni bo'shatadigan joy YO'Q — kesh
 * `useEffect` ning tozalash funksiyasini bilmaydi. Bu yerda rasm
 * komponentning umriga bog'langan: qator ekrandan ketdi — havola ham
 * bo'shatildi.
 */
export function AuthedImage({
  url,
  present,
  width,
  height,
  alt,
  emptyHint,
  onClick,
}: AuthedImageProps) {
  const { t } = useTranslation()
  const [src, setSrc] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const [missing, setMissing] = useState(false)

  useEffect(() => {
    if (!present) {
      setSrc(null)
      setFailed(false)
      setMissing(false)
      return
    }

    let objectUrl: string | null = null
    let cancelled = false
    const controller = new AbortController()

    setFailed(false)
    setMissing(false)
    fetchImageBlob(url, controller.signal)
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setSrc(objectUrl)
      })
      .catch((error: unknown) => {
        // ⚠️ Xato matni bu yerda KO'RSATILMAYDI, faqat belgi qo'yiladi.
        // Jadvalda 25 ta rasm bor va ularning har biri o'z xato matnini
        // chizsa jadval o'qib bo'lmas holga kelardi. 404 — faylning o'zi
        // yo'q, ya'ni «rasm yo'q» bilan bir xil.
        if (cancelled) return
        if (errorStatus(error) === 404) setMissing(true)
        else setFailed(true)
      })

    return () => {
      cancelled = true
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [url, present])

  const box = { width, height, borderRadius: 4, objectFit: 'cover' as const }

  if (!present || missing || failed) {
    return (
      <Tooltip title={failed ? t('attendance.photoFailedHint') : (emptyHint ?? t('attendance.noPhotoHint'))}>
        <Avatar
          shape="square"
          icon={<UserOutlined />}
          alt={failed ? t('attendance.photoFailed') : t('attendance.noPhoto')}
          style={{ width, height, lineHeight: `${height}px` }}
        />
      </Tooltip>
    )
  }

  if (src === null) return <Skeleton.Image active style={{ width, height }} />

  return (
    <img
      src={src}
      alt={alt}
      style={{ ...box, cursor: onClick ? 'zoom-in' : 'default' }}
      {...(onClick ? { onClick } : {})}
    />
  )
}
